/**
 * CardOnFilePayment — issue #29 AC1
 *
 * Staff-driven card-on-file charge. Fetches the customer's saved
 * payment methods and renders a one-click charge button. Total is
 * always server-recalculated — client only sends `bookingId` + `tipCents`
 * + the chosen `savedCardId`.
 *
 * Security: card data never lives in client state. Saved cards are
 * referenced by Stripe payment_method id; the actual PAN was tokenized
 * on the customer-app side and is held by Stripe.
 */
import { useCallback, useEffect, useState } from 'react';
import {
  adminPaymentsApi,
  type AdminSavedCard,
} from '../../lib/api.js';
import { makeIdempotencyKey } from '../../lib/idempotency.js';

type CardOnFilePaymentProps = {
  readonly apiUrl: string;
  readonly customerId: string;
  readonly bookingId: string;
  readonly totalCents: number;
  readonly tipCents: number;
  /**
   * Ted #34 E6.3 (#1161) — set when the tip came from a 15 / 18 / 20 %
   * button; the server checks the cents against it and records it. Null /
   * omitted for a typed tip or no tip.
   */
  readonly tipPercent?: number | null;
  readonly onSuccess: (paymentId: string) => void;
  readonly onCancel: () => void;
  /**
   * Optional caller-supplied idempotency key (#251) — lets a wrapping
   * settle flow pin ONE key to the whole logical attempt. When omitted,
   * a stable per-mount key is minted (NOT per click — see below).
   */
  readonly idempotencyKey?: string;
  /**
   * #251 should-fix 8 — busy reporting. True while the charge POST is in
   * flight so a wrapping dialog (SettleNowDialog) can LOCK dismissal:
   * closing mid-charge with a "stays in the queue" notice was false
   * (the charge may complete and settle the invoice after the close).
   */
  readonly onBusyChange?: (busy: boolean) => void;
};

type FlowState =
  | { kind: 'loading-cards' }
  | { kind: 'no-cards' }
  | { kind: 'ready'; cards: AdminSavedCard[]; selectedCardId: string }
  | { kind: 'charging'; cardId: string }
  | { kind: 'succeeded'; paymentId: string }
  | { kind: 'error'; message: string };

function formatMoney(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

function brandLabel(brand: string): string {
  switch (brand.toLowerCase()) {
    case 'visa':
      return 'Visa';
    case 'mastercard':
      return 'Mastercard';
    case 'amex':
    case 'american_express':
      return 'Amex';
    case 'discover':
      return 'Discover';
    default:
      return brand.charAt(0).toUpperCase() + brand.slice(1);
  }
}

export function CardOnFilePayment({
  apiUrl,
  customerId,
  bookingId,
  totalCents,
  tipCents,
  tipPercent = null,
  onSuccess,
  onCancel,
  idempotencyKey,
  onBusyChange,
}: CardOnFilePaymentProps): JSX.Element {
  const [state, setState] = useState<FlowState>({ kind: 'loading-cards' });

  // #251 should-fix 8 — surface the in-flight charge to the wrapper.
  // Cleanup releases the lock on unmount: a successful charge flips the
  // wrapping dialog to its success branch (which unmounts this component)
  // in the SAME commit, so a busy=false effect would otherwise never run
  // and the wrapper would stay locked.
  const busy = state.kind === 'charging';
  useEffect(() => {
    onBusyChange?.(busy);
    return () => {
      if (busy) onBusyChange?.(false);
    };
  }, [busy, onBusyChange]);
  // #251 — ONE stable idempotency key per logical attempt (per mount), NOT
  // per click. A deliberate staff retry after a failure re-sends the SAME
  // key: after a definitive decline the server deleted the pending stub so
  // the key is reusable; after an indeterminate outcome the surviving stub
  // BLOCKS the replay server-side (which is exactly the protection a fresh
  // per-click key would have bypassed, risking a double charge).
  const [attemptKey] = useState<string>(
    () => idempotencyKey ?? makeIdempotencyKey('card-on-file', bookingId),
  );

  const loadCards = useCallback(async () => {
    setState({ kind: 'loading-cards' });
    const result = await adminPaymentsApi.getSavedCards(apiUrl, customerId);
    if (result.error || !result.data) {
      setState({
        kind: 'error',
        message: result.error ?? 'Could not load saved cards',
      });
      return;
    }
    if (result.data.cards.length === 0) {
      setState({ kind: 'no-cards' });
      return;
    }
    const defaultCard =
      result.data.cards.find((c) => c.isDefault) ?? result.data.cards[0];
    setState({
      kind: 'ready',
      cards: result.data.cards,
      selectedCardId: defaultCard?.id ?? '',
    });
  }, [apiUrl, customerId]);

  useEffect(() => {
    void loadCards();
  }, [loadCards]);

  const handleSelectCard = useCallback((id: string) => {
    setState((prev) =>
      prev.kind === 'ready' ? { ...prev, selectedCardId: id } : prev,
    );
  }, []);

  const handleCharge = useCallback(async () => {
    if (state.kind !== 'ready' || !state.selectedCardId) return;
    setState({ kind: 'charging', cardId: state.selectedCardId });
    const result = await adminPaymentsApi.chargeCardOnFile(apiUrl, {
      bookingId,
      savedCardId: state.selectedCardId,
      tipCents,
      ...(tipPercent !== null && tipCents > 0 ? { tipPercent } : {}),
      idempotencyKey: attemptKey,
      // #522 review — the amount THIS SCREEN quoted. The server never
      // charges it; it compares against its own derivation and refuses
      // before the gateway. An applied loyalty redemption lowers the
      // displayed total but not the booking row, so without this the
      // card is charged MORE than the desk quoted.
      expectedTotalCents: totalCents + tipCents,
    });
    if (result.error || !result.data) {
      setState({
        kind: 'error',
        message: result.error ?? 'Charge failed',
      });
      return;
    }
    setState({ kind: 'succeeded', paymentId: result.data.paymentId });
    onSuccess(result.data.paymentId);
  }, [apiUrl, bookingId, tipCents, tipPercent, totalCents, state, onSuccess, attemptKey]);

  const totalWithTip = totalCents + tipCents;

  return (
    <section
      className="ferro-card"
      aria-label="Card on file payment"
      data-testid="card-on-file-payment"
    >
      <header className="ferro-card__header">
        <h3 className="ferro-section__subtitle">Card on file</h3>
        <span
          className="ferro-badge ferro-badge--neutral"
          data-testid="card-on-file-total"
        >
          {formatMoney(totalWithTip)}
        </span>
      </header>

      {state.kind === 'loading-cards' ? (
        <p
          className="ferro-muted"
          role="status"
          data-testid="card-on-file-status"
        >
          Loading saved cards…
        </p>
      ) : null}

      {state.kind === 'no-cards' ? (
        <div data-testid="card-on-file-no-cards">
          <p>This customer has no saved cards.</p>
          <p className="ferro-muted">
            Use Manual Entry or Tap-to-Pay to take payment, then offer to
            save the card for future bookings.
          </p>
        </div>
      ) : null}

      {state.kind === 'ready' ? (
        <div className="ferro-form" data-testid="card-on-file-form">
          <p className="ferro-muted">Select a card to charge:</p>
          {state.cards.map((card) => (
            <label
              key={card.id}
              className="ferro-form__row"
              data-testid={`card-on-file-card-${card.id}`}
            >
              <input
                type="radio"
                name="savedCard"
                value={card.id}
                checked={state.selectedCardId === card.id}
                onChange={() => handleSelectCard(card.id)}
              />
              <span>
                {/* Bank (eCheck) CIM profiles carry expMonth/expYear 0 —
                    render a clean label instead of "(exp 00/0)" (#248). */}
                {card.brand.toLowerCase() === 'bank' ? (
                  <>Bank account ····{card.last4}</>
                ) : (
                  <>
                    {brandLabel(card.brand)} ····{card.last4} (exp{' '}
                    {String(card.expMonth).padStart(2, '0')}/
                    {String(card.expYear).slice(-2)})
                  </>
                )}
                {card.isDefault ? ' · default' : ''}
              </span>
            </label>
          ))}
        </div>
      ) : null}

      {state.kind === 'charging' ? (
        <p
          className="ferro-muted"
          role="status"
          data-testid="card-on-file-status"
        >
          Charging card…
        </p>
      ) : null}

      {state.kind === 'succeeded' ? (
        <div
          className="ferro-form__success"
          role="status"
          data-testid="card-on-file-success"
        >
          <p>Payment successful. Payment ID: <code>{state.paymentId}</code></p>
        </div>
      ) : null}

      {state.kind === 'error' ? (
        <div
          className="ferro-dashboard__error"
          role="alert"
          data-testid="card-on-file-error"
        >
          <p>{state.message}</p>
          <button
            type="button"
            className="ferro-btn ferro-btn--ghost"
            onClick={() => void loadCards()}
            data-testid="card-on-file-retry"
          >
            Retry
          </button>
        </div>
      ) : null}

      <footer className="ferro-card__footer">
        {state.kind === 'ready' ? (
          <button
            type="button"
            className="ferro-btn ferro-btn--primary"
            onClick={() => void handleCharge()}
            disabled={!state.selectedCardId}
            data-testid="card-on-file-charge-button"
          >
            Charge {formatMoney(totalWithTip)}
          </button>
        ) : null}
        <button
          type="button"
          className="ferro-btn ferro-btn--ghost"
          onClick={onCancel}
          disabled={state.kind === 'charging'}
          data-testid="card-on-file-cancel"
        >
          Back
        </button>
      </footer>
    </section>
  );
}
